import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import { setCurrentUser } from './user.duck';

/**
 * Favorites are stored as an array of listing ids (plain strings) in the
 * current user's privateData, which Sharetribe only ever exposes to the
 * user themselves - no separate backend storage needed.
 */

export const getFavoriteListingIds = currentUser =>
  currentUser?.attributes?.profile?.privateData?.favoriteListingIds || [];

export const isListingFavorited = (currentUser, listingId) =>
  getFavoriteListingIds(currentUser).includes(listingId);

// ================ Async Thunks ================ //

const toggleFavoriteListingThunk = createAsyncThunk(
  'favorites/toggleFavoriteListing',
  ({ listingId }, { getState, dispatch, extra: sdk, rejectWithValue }) => {
    const { currentUser } = getState().user;
    const currentIds = getFavoriteListingIds(currentUser);
    const wasFavorited = currentIds.includes(listingId);
    const favoriteListingIds = wasFavorited
      ? currentIds.filter(id => id !== listingId)
      : [...currentIds, listingId];

    return sdk.currentUser
      .updateProfile(
        { privateData: { favoriteListingIds } },
        {
          expand: true,
          include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
        }
      )
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        dispatch(setCurrentUser(entities[0]));
        return { listingId };
      })
      .catch(e => rejectWithValue({ listingId, error: storableError(e) }));
  }
);

export const toggleFavoriteListing = listingId => dispatch =>
  dispatch(toggleFavoriteListingThunk({ listingId })).unwrap();

// ================ Slice ================ //

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState: {
    togglingListingId: null,
    toggleError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(toggleFavoriteListingThunk.pending, (state, action) => {
        state.togglingListingId = action.meta.arg.listingId;
        state.toggleError = null;
      })
      .addCase(toggleFavoriteListingThunk.fulfilled, state => {
        state.togglingListingId = null;
      })
      .addCase(toggleFavoriteListingThunk.rejected, (state, action) => {
        state.togglingListingId = null;
        state.toggleError = action.payload?.error;
      });
  },
});

export default favoritesSlice.reducer;
